import { Box, HStack, Icon, InlineBanner, Text } from '@cad-arc';
import React from 'react';

export type RecommendedSectionHeaderState = 'loading' | 'failed' | 'none';

export interface RecommendedSectionHeaderContext {
    /** loading → gathering placeholder; failed → not-available banner; none → render nothing. */
    state: RecommendedSectionHeaderState;
    onRetry: () => void;
    /** Registers the placeholder's DOM element so the peek-chip visibility observer can watch it. */
    registerEl: (el: HTMLElement | null) => void;
}

/**
 * The inline Recommended section, rendered as the GroupedVirtuoso `Header` (flag-on only). It is the
 * section's placeholder WHILE the plan computes (a spinning "Gathering recommended units" row) or
 * AFTER it fails (the "not available" + Retry banner). Once recommendations land this returns null and
 * the store's RECOMMENDED group renders the real, virtualized rows. Living inside the scroller keeps a
 * single scrollbar. Must be a stable component reference (see the drawer's memoized `components`).
 */
export function RecommendedSectionHeader({
    context,
}: {
    context?: RecommendedSectionHeaderContext;
}): React.ReactElement | null {
    if (!context || context.state === 'none') {
        return null;
    }
    return (
        <Box ref={context.registerEl}>
            {/* The "Recommended" section header. Inlined (rather than reusing <GroupHeader/>) so the
                shared group-header.tsx stays byte-identical to `main` on the flag-off path — this
                section is flag-on only. Markup mirrors GroupHeader minus the count (Decision D14). */}
            <Box bg="surface.primary" w="full" py="2">
                <Text tabIndex={-1} variant="bodyMd" fontWeight="bold" color="secondary">
                    Recommended
                </Text>
            </Box>
            {context.state === 'loading' && (
                <HStack
                    gap="2"
                    paddingY="3"
                    width="100%"
                    justifyContent="center"
                    alignItems="center"
                    data-testid="gathering-recommended-units"
                >
                    {/* Sync glyph spun via the global `cad-spin` keyframe through inline style — NOT an
                        `sx`/emotion animation, which does not resolve in the Electron renderer.
                        `reverse` spins CCW to follow the glyph's arrowheads. This is the only spinner;
                        the peek chips use a static icon. */}
                    <Icon
                        icon="Sync"
                        size="sm"
                        color="text.tertiary"
                        style={{
                            animation: 'cad-spin 1s linear infinite reverse',
                            transformOrigin: 'center',
                        }}
                    />
                    <Text variant="bodyMd" color="tertiary">
                        Gathering recommended units
                    </Text>
                </HStack>
            )}
            {context.state === 'failed' && (
                <Box paddingY="1" paddingRight="16px" data-testid="recommended-units-failed">
                    <InlineBanner
                        status="attention"
                        description="Recommendations not available"
                        actions={[
                            {
                                children: 'Retry',
                                onClick: context.onRetry,
                            },
                        ]}
                    />
                </Box>
            )}
        </Box>
    );
}
